import {
    ChangeDetectionStrategy,
    Component,
    computed,
    forwardRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    signal,
} from '@angular/core';
import { IDynamicElement } from '../_shared/models/dynamic/dynamic-element.model';
import { SectionService } from './section.service';
import { DynamicElementService } from '../_shared/models/dynamic/dynamic-element-service.model';
import { DynamicComponent } from '../_shared/types/dynamic-component.type';
import { ISection } from '../_shared/models/section.model';
import { ISpacers } from '../_shared/models/spacers.model';
import { SectionBackground } from '../_shared/types/section-background.type';
import { SectionSpacing } from '../_shared/types/section-spacing.type';
import { IConfig } from '../_shared/models/dynamic/config.model';
import { SurfaceMode } from '../_shared/types/surface-mode.type';
import { normalizeSectionBackground, SECTION_SURFACE_MODE } from '../_shared/data/section-backgrounds.data';
import { v4 as uuidv4 } from 'uuid';
import { SpacerComponent } from '../spacer/spacer.component';
import { SECTION_SURFACE, SectionSurface } from './section-surface';

import { DynamicElementDirective } from '../_shared/directives/dynamic-element.directive';

const NO_BOTTOM_SPACER = ['spacer', 'anchor', 'breadcrumb', 'tabs', 'article-info-bar', 'commerce-partner-page'];
const NO_TOP_SPACER = ['commerce-card-overview-component']

/** Lets a dynamic component opt out of section spacing with a static `sectionSpacing`. */
interface SectionSpacingProvider {
    sectionSpacing?(config: IConfig): SectionSpacing;
}

@Component({
    selector: 'sirius-section',
    templateUrl: './section.component.html',
    styleUrls: ['./section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpacerComponent, DynamicElementDirective],
    providers: [{ provide: SECTION_SURFACE, useExisting: forwardRef(() => SectionComponent) }],
    host: {
        //TODO: Replace this dirty cowboy - kfch
        '[class]': 'componentNameClass',
        '[class.is-default]': "resolvedBackground === 'default'",
        '[class.is-subtle]': "resolvedBackground === 'subtle'",
        '[class.is-inverse]': "resolvedBackground === 'inverse'",
        '[class.is-brand]': "resolvedBackground === 'brand'",
        '[class.is-light]': "ownSurfaceMode === 'light'",
        '[class.is-dark]': "ownSurfaceMode === 'dark'",
    },
})
export class SectionComponent implements OnInit, IDynamicElement, OnDestroy, SectionSurface {
    @Input() config?: ISection;
    @Input() spacers?: ISpacers;
    /** Background for a section that wraps projected content instead of rendering a `config`. */
    @Input() background?: SectionBackground | (string & {});

    private _sectionService = inject(SectionService);
    private _dynamicElementService = inject(DynamicElementService, { optional: true });
    private _parentSurface = inject(SECTION_SURFACE, { optional: true, skipSelf: true });

    private _backgroundValue = 'default';
    private _background = signal<SectionBackground | undefined>(undefined);
    private _component?: DynamicComponent;
    private _componentName = '';
    private _id = uuidv4();

    get componentNameClass(): string {
        return this._componentName;
    }

    get resolvedBackground(): SectionBackground | undefined {
        return this._background();
    }

    /** The mode this section's own background sets. */
    get ownSurfaceMode(): SurfaceMode | undefined {
        const background = this._background();
        return background && SECTION_SURFACE_MODE[background];
    }

    /** Its own mode, or the one inherited from a parent section. */
    readonly surfaceMode = computed(() => this.ownSurfaceMode ?? this._parentSurface?.surfaceMode());

    /** @deprecated Use `resolvedBackground`. */
    get backgroundColor(): string {
        return this._background() ?? this._backgroundValue;
    }

    ngOnInit(): void {
        if (!this.config) {
            this.setBackground(this.background);
            return;
        }

        this.setBackground(this.config.background?.color);
        this._componentName = this.config.componentName;

        if (this._dynamicElementService) {
            this._component = this._dynamicElementService.getComponent(this.config.componentName);
        }

        const flush = this.spacing(this.config) === 'flush';
        this._sectionService.addBackgroundColor(this._id, this.backgroundColor, flush);

        if (this.spacers) {
            return;
        }

        if (flush) {
            this.spacers = { top: false, bottom: false };
            return;
        }

        //TODO: Allow easier control of when to have no top/bottom spacers
        this.spacers = this._sectionService.getSpacers(
            this._id,
            !(this._component && NO_BOTTOM_SPACER.includes(this._component?.type)),
            !(this._component && NO_TOP_SPACER.includes(this._component?.type))
        );
    }

    ngOnDestroy() {
        this._sectionService.removeBackgroundColor(this._id);
    }

    private spacing(config: ISection): SectionSpacing {
        const component = this._component?.component as SectionSpacingProvider | undefined;
        return component?.sectionSpacing?.(config) ?? 'default';
    }

    private setBackground(value?: string) {
        this._backgroundValue = value || 'default';
        this._background.set(normalizeSectionBackground(value));
    }
}
